import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  FolderIcon as FolderIconSolid,
  FolderOpenIcon,
} from '@heroicons/react/24/solid';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  blobSinkActionService,
  useClioImportDocumentsMutation,
  useClioRefreshTreeMutation,
  useDropboxImportDocumentsMutation,
  useDropboxRefreshTreeMutation,
  useGoogleDriveImportDocumentsMutation,
  useGoogleDriveRefreshTreeMutation,
} from '@/services/api/blobSinkActionService';
import {
  BlobSinkActionsTreeApiArg,
  SinkFileNodeSchema,
  SinkFolderNodeSchema,
} from '@/services/api/generated/blobSinkActionService';
import {
  DocumentSyncTargetSystem,
  getExternalSystemDisplayName,
} from '@/utils/externalSystemUtils';

import { treeStyles } from '../constants/treeStyles';
import { getFileIcon } from '../utils/fileIcons';
import { formatDateString, formatFileSize } from '../utils/fileUtils';

/** Extract the file extension from a filename (e.g. "report.pdf" → "pdf"). */
function extractExtension(name: string): string | null {
  const dotIndex = name.lastIndexOf('.');
  return dotIndex > 0 ? name.slice(dotIndex + 1).toLowerCase() : null;
}

interface ExternalSystemTreeViewProps {
  matterId: string;
  targetSystem: DocumentSyncTargetSystem;
}

type TreeNode =
  | ({ type: 'file' } & SinkFileNodeSchema)
  | ({ type: 'folder' } & SinkFolderNodeSchema);

interface TreeNodeItemProps {
  node: TreeNode;
  level: number;
  expandedFolders: Set<string>;
  onToggleFolder: (id: string) => void;
  onImport?: (internalId: string) => void;
  importingIds?: Set<string>;
}

const TreeNodeItem = ({
  node,
  level,
  expandedFolders,
  onToggleFolder,
  onImport,
  importingIds,
}: TreeNodeItemProps) => {
  const nodeId = node.external_id || node.name;
  const isFolder = node.type === 'folder';
  const isExpanded = expandedFolders.has(nodeId);
  const folderNode = node as { type: 'folder' } & SinkFolderNodeSchema;
  const fileNode = node as { type: 'file' } & SinkFileNodeSchema;
  const hasContent = isFolder && folderNode.children?.length > 0;
  const isSynced = !isFolder && fileNode.is_synced;
  const internalId = !isFolder ? fileNode.internal_id : null;
  const isImporting = internalId ? importingIds?.has(internalId) : false;

  const handleClick = () => {
    if (isFolder) {
      onToggleFolder(nodeId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleImport = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (internalId && onImport) {
      onImport(internalId);
    }
  };

  // Match the existing tree item styling
  const sizeStyles = treeStyles.row.sm;
  const indent = level * 12;

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        className={`group relative flex items-center ${sizeStyles.class} ${sizeStyles.padding} ${sizeStyles.fontSize} rounded-sm cursor-pointer hover:bg-gray-100`}
        style={{ paddingLeft: `${indent + 8}px` }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {isFolder ? (
          <>
            {/* eslint-disable-next-line no-nested-ternary */}
            {!hasContent ? (
              <span className="w-4 flex-shrink-0" />
            ) : isExpanded ? (
              <ChevronDownIcon className="h-3 w-3 text-gray-400 flex-shrink-0 mr-1" />
            ) : (
              <ChevronRightIcon className="h-3 w-3 text-gray-400 flex-shrink-0 mr-1" />
            )}
            {isExpanded ? (
              <FolderOpenIcon className="h-4 w-4 text-blue-400 flex-shrink-0 mr-1.5" />
            ) : (
              <FolderIconSolid
                className={`h-4 w-4 ${hasContent ? 'text-blue-400' : 'text-blue-400/50'} flex-shrink-0 mr-1.5`}
              />
            )}
            <span className="truncate font-medium select-none">
              {node.name}
            </span>
            {hasContent && (
              <span className="text-[10px] text-muted-foreground ml-1">
                ({folderNode.children.length})
              </span>
            )}
          </>
        ) : (
          <>
            <span className="w-4 flex-shrink-0" />
            {getFileIcon(extractExtension(node.name))}
            <span className="truncate font-medium select-none flex-1 min-w-0">
              {node.name}
            </span>

            {/* Sync status + actions */}
            <span className="flex items-center gap-1 flex-shrink-0 ml-2">
              {/* eslint-disable-next-line no-nested-ternary */}
              {isSynced ? (
                <CheckCircleIcon
                  className="h-3.5 w-3.5 text-green-500"
                  title="Imported to Finch"
                />
              ) : isImporting ? (
                <span className="text-[10px] text-muted-foreground animate-pulse">
                  Importing...
                </span>
              ) : (
                internalId &&
                onImport && (
                  <button
                    type="button"
                    onClick={handleImport}
                    className="hidden group-hover:flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-800 whitespace-nowrap"
                    title="Import to Finch"
                  >
                    <ArrowDownTrayIcon className="h-3 w-3" />
                    Import
                  </button>
                )
              )}

              {fileNode.size_bytes != null && (
                <span className="text-[10px] text-muted-foreground">
                  {formatFileSize(fileNode.size_bytes)}
                </span>
              )}
            </span>
          </>
        )}
      </div>

      {isFolder && isExpanded && folderNode.children && (
        <div>
          {folderNode.children.map((child, index) => (
            <TreeNodeItem
              key={child.external_id || `${child.name}-${index}`}
              node={child}
              level={level + 1}
              expandedFolders={expandedFolders}
              onToggleFolder={onToggleFolder}
              onImport={onImport}
              importingIds={importingIds}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Collect all internal_id values from unsynced file nodes in a tree (recursively).
 */
function collectUnsyncedFileIds(nodes: TreeNode[]): string[] {
  return nodes.flatMap((node) => {
    if (node.type === 'file') {
      const fileNode = node as { type: 'file' } & SinkFileNodeSchema;
      return !fileNode.is_synced && fileNode.internal_id
        ? [fileNode.internal_id]
        : [];
    }
    if (node.type === 'folder') {
      const folderNode = node as { type: 'folder' } & SinkFolderNodeSchema;
      return folderNode.children
        ? collectUnsyncedFileIds(folderNode.children as TreeNode[])
        : [];
    }
    return [];
  });
}

export const ExternalSystemTreeView = ({
  matterId,
  targetSystem,
}: ExternalSystemTreeViewProps) => {
  const dispatch = useDispatch();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [importingIds, setImportingIds] = useState<Set<string>>(new Set());
  const [importAllLoading, setImportAllLoading] = useState(false);

  // Get display name for the target system
  const cmsDisplayName = getExternalSystemDisplayName(targetSystem);

  // Note: Cast needed because generated types may not include CLIO/NEOS yet
  const {
    data: treeData,
    isLoading,
    error,
    refetch,
  } = blobSinkActionService.useBlobSinkActionsTreeQuery(
    { matterId, targetSystem } as BlobSinkActionsTreeApiArg,
    { skip: !matterId },
  );

  const [importDropboxDocuments] = useDropboxImportDocumentsMutation();
  const [importClioDocuments] = useClioImportDocumentsMutation();
  const [importGoogleDriveDocuments] = useGoogleDriveImportDocumentsMutation();
  const [refreshDropboxTree] = useDropboxRefreshTreeMutation();
  const [refreshClioTree] = useClioRefreshTreeMutation();
  const [refreshGoogleDriveTree] = useGoogleDriveRefreshTreeMutation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      if (targetSystem === 'DROPBOX') {
        await refreshDropboxTree({ matterId }).unwrap();
      } else if (targetSystem === 'CLIO') {
        await refreshClioTree({ matterId }).unwrap();
      } else if (targetSystem === 'GOOGLE_DRIVE') {
        await refreshGoogleDriveTree({ matterId }).unwrap();
      }
      dispatch(blobSinkActionService.util.invalidateTags(['blob-sink-actions']));
      refetch();
    } catch {
      // Error is handled by RTK Query
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch, matterId, targetSystem, refreshDropboxTree, refreshClioTree, refreshGoogleDriveTree, refetch]);

  const handleToggleFolder = useCallback((id: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleImportSingle = useCallback(
    async (internalId: string) => {
      setImportingIds((prev) => new Set(prev).add(internalId));
      try {
        if (targetSystem === 'DROPBOX') {
          await importDropboxDocuments({
            matterId,
            dropboxDocumentIds: [internalId],
          }).unwrap();
        } else if (targetSystem === 'CLIO') {
          await importClioDocuments({
            matterId,
            clioDocumentIds: [internalId],
          }).unwrap();
        } else if (targetSystem === 'GOOGLE_DRIVE') {
          await importGoogleDriveDocuments({
            matterId,
            googleDriveDocumentIds: [internalId],
          }).unwrap();
        }
        // Refresh the tree to show updated sync status
        refetch();
      } catch {
        // Error is handled by RTK Query
      } finally {
        setImportingIds((prev) => {
          const next = new Set(prev);
          next.delete(internalId);
          return next;
        });
      }
    },
    [matterId, targetSystem, importDropboxDocuments, importClioDocuments, importGoogleDriveDocuments, refetch],
  );

  // Collect all unsynced file IDs for "Import All"
  const unsyncedIds = useMemo(() => {
    if (!treeData?.root) return [];
    return collectUnsyncedFileIds(treeData.root as TreeNode[]);
  }, [treeData]);

  const handleImportAll = useCallback(async () => {
    if (unsyncedIds.length === 0) return;
    setImportAllLoading(true);
    setImportingIds((prev) => new Set([...prev, ...unsyncedIds]));
    try {
      if (targetSystem === 'DROPBOX') {
        await importDropboxDocuments({
          matterId,
          dropboxDocumentIds: unsyncedIds,
        }).unwrap();
      } else if (targetSystem === 'CLIO') {
        await importClioDocuments({
          matterId,
          clioDocumentIds: unsyncedIds,
        }).unwrap();
      } else if (targetSystem === 'GOOGLE_DRIVE') {
        await importGoogleDriveDocuments({
          matterId,
          googleDriveDocumentIds: unsyncedIds,
        }).unwrap();
      }
      refetch();
    } catch {
      // Error is handled by RTK Query
    } finally {
      setImportAllLoading(false);
      setImportingIds((prev) => {
        const next = new Set(prev);
        unsyncedIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [matterId, targetSystem, unsyncedIds, importDropboxDocuments, importClioDocuments, importGoogleDriveDocuments, refetch]);

  const hasRefresh = targetSystem === 'DROPBOX' || targetSystem === 'CLIO' || targetSystem === 'GOOGLE_DRIVE';
  const hasImport = targetSystem === 'DROPBOX' || targetSystem === 'CLIO' || targetSystem === 'GOOGLE_DRIVE';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        Loading {cmsDisplayName} files...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-xs">
        <span className="text-destructive">
          Failed to load {cmsDisplayName} files
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-blue-600 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (targetSystem === 'GOOGLE_DRIVE' && treeData && !treeData.external_matter_id) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-1 text-xs text-muted-foreground px-4 text-center">
        <span>Add a Google Drive folder URL to this matter to view synced files.</span>
      </div>
    );
  }

  if (!treeData || treeData.root.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-xs text-muted-foreground">
        <span>No files in {cmsDisplayName}</span>
        {hasRefresh && (
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1"
          >
            <ArrowPathIcon
              className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`}
            />
            {isRefreshing ? 'Refreshing...' : `Refresh from ${cmsDisplayName}`}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${treeStyles.container.width} border-r border-border overflow-y-auto h-full w-full`}
    >
      {/* Header with metadata */}
      <div className="sticky top-0 bg-background border-b px-2 py-1.5 text-[10px] text-muted-foreground flex items-center justify-between">
        <span>
          {treeData.external_matter_id &&
            `Project: ${treeData.external_matter_id}`}
        </span>
        <span className="flex items-center gap-2">
          {hasRefresh && (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-0.5"
              title={`Refresh from ${cmsDisplayName}`}
            >
              <ArrowPathIcon
                className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          )}
          {hasImport && unsyncedIds.length > 0 && (
            <button
              type="button"
              onClick={handleImportAll}
              disabled={importAllLoading}
              className="text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-0.5"
            >
              <ArrowDownTrayIcon className="h-3 w-3" />
              {importAllLoading
                ? 'Importing...'
                : `Import All (${unsyncedIds.length})`}
            </button>
          )}
          {treeData.retrieved_at && (
            <span>{formatDateString(treeData.retrieved_at, 'relative')}</span>
          )}
        </span>
      </div>

      {/* Tree content */}
      <div className="py-1">
        {treeData.root.map((node, index) => (
          <TreeNodeItem
            key={node.external_id || `${node.name}-${index}`}
            node={node}
            level={0}
            expandedFolders={expandedFolders}
            onToggleFolder={handleToggleFolder}
            onImport={hasImport ? handleImportSingle : undefined}
            importingIds={importingIds}
          />
        ))}
      </div>
    </div>
  );
};
